import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MarketingLayout, { Seo, Reveal, PageHero, FinalCta } from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Quotes, RocketLaunch, Check } from "@phosphor-icons/react";
import { SuccessStoryCard } from "@/components/marketing/SuccessStoryCard";
import { publicStories } from "@/lib/api";

const HIGHLIGHTS = [
  { title: "31 posts in 90 seconds", desc: "The AI Monthly Planner generated a complete, varied August calendar for a property maintenance brand — promos, tips, holiday posts and stories — in a minute and a half." },
  { title: "A real store, really connected", desc: "Vozelto runs live against an 89-product Shopify store — real product photos in the Image Studio, real sales data in the dashboards, real publishing to Meta." },
  { title: "100-point quality gate", desc: "Every design is scored by AI vision for brand consistency, readability and CTA strength before a human ever reviews it." },
];

export default function StoriesPage() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  useEffect(() => { publicStories().then(setStories).catch(() => {}); }, []);
  return (
    <MarketingLayout>
      <Seo title="Success Stories — Vozelto Early Access" path="/stories"
        description="Vozelto is launching with an early access cohort. Verified customer results will be published here — no invented testimonials, ever. Become a founding customer." />
      <PageHero caps="Success stories" title={<>Launching soon.<br /><span className="brand-text">Truthfully.</span></>}
        subtitle="We don't publish invented testimonials or fake logos. This page will fill with verified results from our launch cohort — and one of these spots could be yours." />

      <section className="max-w-6xl mx-auto px-5 pb-16">
        {stories.length > 0 && (
          <div className="mb-14 space-y-6" data-testid="success-stories-list">
            <Reveal className="max-w-2xl">
              <div className="label-caps mb-3">Case studies</div>
              <h2 className="font-head font-bold text-3xl tracking-tight">How businesses run on Vozelto</h2>
              {stories.some((s) => s.placeholder) && (
                <p className="mt-2 text-xs text-[#7a7a7a]">Stories marked <span className="text-amber-300 font-semibold">SAMPLE</span> are illustrative and will be replaced by verified customers.</p>
              )}
            </Reveal>
            {stories.map((s, i) => <Reveal key={s.id}><SuccessStoryCard s={s} i={i} /></Reveal>)}
          </div>
        )}
        <Reveal className="mb-10">
          <div className="card-matte brand-glow p-8 md:p-10 relative overflow-hidden" data-testid="early-adopter-invite">
            <div className="absolute -top-16 right-0 h-40 w-40 rounded-full bg-[#6B3DFF]/15 blur-[80px] pointer-events-none" />
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#6B3DFF]/15 flex items-center justify-center shrink-0">
                <RocketLaunch size={24} className="brand-text" weight="duotone" />
              </div>
              <div>
                <h2 className="font-head font-bold text-2xl">Become a founding customer</h2>
                <p className="mt-2 text-[#b5b5b5] leading-relaxed max-w-2xl">Early adopters get direct access to the team building Vozelto, input on the roadmap, and — if you're willing — a featured spot on this page with your real results.</p>
                <ul className="mt-4 space-y-2">
                  {["14-day free trial with full platform access", "Priority support during onboarding", "Your feedback shapes what we build next"].map((t) => (
                    <li key={t} className="flex items-center gap-2.5 text-sm text-[#cfcfcf]"><Check size={15} className="brand-text" weight="bold" /> {t}</li>
                  ))}
                </ul>
                <Button data-testid="stories-early-cta" onClick={() => navigate("/signup")} className="mt-6 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-7 h-11">
                  Join the Launch Cohort
                </Button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal className="max-w-2xl mb-8">
          <div className="label-caps mb-3">What the platform has already done</div>
          <h2 className="font-head font-bold text-3xl tracking-tight">Real numbers from the product itself</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5" data-testid="product-highlights">
          {HIGHLIGHTS.map((h) => (
            <Reveal key={h.title}>
              <div className="card-matte p-6 h-full">
                <h3 className="font-head font-semibold text-lg brand-text">{h.title}</h3>
                <p className="mt-2 text-sm text-[#b5b5b5] leading-relaxed">{h.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="results-placeholders">
          {["E-commerce", "Local Business", "Agency"].map((seg, i) => (
            <Reveal key={seg}>
              <div data-testid={`result-placeholder-${i}`} className="card-matte p-6 border border-dashed border-white/12 h-full">
                <Quotes size={26} className="text-white/15 mb-3" weight="fill" />
                <p className="text-[#7a7a7a] leading-relaxed text-sm italic">Verified {seg.toLowerCase()} results coming soon — this spot is reserved for a real customer story.</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/5 border border-dashed border-white/15" />
                  <div>
                    <div className="text-sm font-semibold text-[#7a7a7a]">Future customer</div>
                    <div className="text-xs text-[#5a5a5a]">{seg}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <FinalCta title="Write the First Success Story" />
    </MarketingLayout>
  );
}
